import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getContactsSyncJobs } from "../../useCase/listUseCase";
import { getAccountInfo } from "../../utils/authToken";
import {
  clearTrackedContactsSyncJob,
  CONTACTS_SYNC_JOBS_CHANGED,
  CONTACTS_SYNC_JOB_STATES_RECEIVED,
  getTrackedContactsSyncPlatforms,
  publishContactsSyncJobTerminal,
} from "../../utils/contactsSyncJobStore";
import useToast from "./toast/useToast";
import { formatNumber } from "../../utils/numberFormat";
import { getAdSyncPermissionMessage, isAdSyncPermissionResult } from "../../utils/adSyncPermission";

const NETWORK_NAMES = {
  linkedin: "LinkedIn Ads",
  google: "Google Ads",
  meta: "Meta Ads",
};

const TERMINAL_STATUSES = new Set([
  "success",
  "failed",
  "blocked",
  "partial_failure",
  "cancelled",
]);

function formatSyncCount(t, key, count) {
  const numeric = Number(count || 0);
  return t(key, { count: numeric }).replace(String(numeric), formatNumber(numeric));
}

export function buildContactsSyncSuccessToast(t, network, result) {
  if (result?.reason === "no_contacts" && !Number(result.failedCount || 0)) return `${network}: ${t("modal.notifications.inbox.noContactsToSync")}`;
  const hasSummary = result && result.totalLists != null && result.syncedCount != null;
  if (!hasSummary) return t("toastMessages.adSync.success", { network });

  const summary = Number(result.syncedCount || 0) > 0
    ? formatSyncCount(t, "modal.notifications.inbox.audiencesSent", result.syncedCount)
    : t(`modal.notifications.inbox.${Number(result.ignoredCount || 0) + Number(result.noContactsCount || 0) + Number(result.failedCount || 0) > 0 ? "nothingSent" : "alreadyUpdated"}`);
  const failed = Number(result.failedCount || 0) > 0 ? ` · ${formatSyncCount(t, "modal.notifications.inbox.failedSends", result.failedCount)}` : "";
  return `${network}: ${summary}${failed}`;
}

// Unlike the page-level control state, this tracker is mounted with the app
// shell. It owns the terminal toast so a submitted audience sync is reported
// once even if the user navigates away from Contacts.
export default function ContactsSyncJobTracker() {
  const { t } = useTranslation("commonTranslation");
  const { showSuccessToast, showErrorToast } = useToast();
  const polling = useRef(false);

  const processStates = useCallback((accountId, states) => {
    const tracked = getTrackedContactsSyncPlatforms(accountId);
    const platforms = Object.keys(tracked);
    if (!accountId || platforms.length === 0) return;

    platforms.forEach((platform) => {
      const state = states[platform];
      const trackedJobId = Number(tracked[platform]?.jobId || 0);
      const currentJobId = Number(state?.jobId || 0);
      if (trackedJobId && currentJobId !== trackedJobId) return;
      if (!state || state.active || !TERMINAL_STATUSES.has(state.status)) return;

      // Notify mounted controls before rendering the toast. This keeps the
      // button and terminal feedback on the same observed job transition.
      publishContactsSyncJobTerminal(accountId, platform, state);
      clearTrackedContactsSyncJob(accountId, platform);
      const network = NETWORK_NAMES[platform];
      if (state.status === "success") {
        showSuccessToast(buildContactsSyncSuccessToast(t, network, state.result), {
          triggerNotificationsRefresh: true,
        });
        return;
      }

      if (state.status === "partial_failure" && state.result?.syncedCount != null) {
        showErrorToast(buildContactsSyncSuccessToast(t, network, state.result), { triggerNotificationsRefresh: true });
        return;
      }

      if (isAdSyncPermissionResult(state?.result)) {
        showErrorToast(getAdSyncPermissionMessage(t, network));
        return;
      }

      const errorMessage =
        state?.result?.error || t("toastMessages.adSync.error", { network });
      // A provider can fail with an action the user has to take outside
      // LinkedScope (accepting Meta's Custom Audience terms, for example).
      // The job stores that link, so the terminal toast must keep offering it.
      const actionUrl = state?.result?.actionUrl;
      if (actionUrl) {
        showErrorToast(
          <span>
            {errorMessage}{" "}
            <a href={actionUrl} target="_blank" rel="noreferrer">
              {t("buttons.sync.terms.platformLink", { network })}
            </a>
          </span>,
          { sticky: true }
        );
        return;
      }

      showErrorToast(errorMessage);
    });
  }, [showErrorToast, showSuccessToast, t]);

  const poll = useCallback(async () => {
    if (polling.current) return;
    const accountId = getAccountInfo()?.accountId;
    const tracked = getTrackedContactsSyncPlatforms(accountId);
    if (!accountId || Object.keys(tracked).length === 0) return;

    polling.current = true;
    try {
      const response = await getContactsSyncJobs();
      if (Number(getAccountInfo()?.accountId) !== Number(accountId)) return;
      processStates(accountId, response?.data?.data || {});
    } catch (_) {
      // A temporary status error must not discard durable tracking. The next
      // app-level poll will retry while the entry remains in local storage.
    } finally {
      polling.current = false;
    }
  }, [processStates]);

  useEffect(() => {
    const handleStatesReceived = (event) => {
      const currentAccountId = Number(getAccountInfo()?.accountId || 0);
      const eventAccountId = Number(event?.detail?.accountId || 0);
      if (!currentAccountId || eventAccountId !== currentAccountId) return;
      processStates(currentAccountId, event?.detail?.states || {});
    };

    poll();
    const interval = window.setInterval(poll, 3000);
    window.addEventListener(CONTACTS_SYNC_JOBS_CHANGED, poll);
    window.addEventListener(CONTACTS_SYNC_JOB_STATES_RECEIVED, handleStatesReceived);
    window.addEventListener("storage", poll);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(CONTACTS_SYNC_JOBS_CHANGED, poll);
      window.removeEventListener(CONTACTS_SYNC_JOB_STATES_RECEIVED, handleStatesReceived);
      window.removeEventListener("storage", poll);
    };
  }, [poll, processStates]);

  return null;
}
